import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Add,
  Delete,
  Search,
  FilterList,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Done,
  Visibility,
} from '@mui/icons-material';
import useApi from '../../hooks/useApi';
import { API_BASE_URL, ENDPOINTS } from '../../config/api';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Notification from '../../components/ui/Notification';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StatusBadge from '../../components/ui/StatusBadge';

const BookingListPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    booking_type: '',
    custom_price_enabled: '',
    payment_status: 'paid', // Show only paid orders by default
  });
  const [showFilters, setShowFilters] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  // Fetch bookings
  const {
    data: bookingsData,
    loading: loadingBookings,
    error: bookingsError,
    fetchData: fetchBookings,
    deleteData: deleteBooking,
  } = useApi(ENDPOINTS.BOOKINGS, {
    fetchOnMount: true,
    params: {
      page: currentPage,
      search: searchTerm,
      ...filters,
    },
  });

  // Log errors
  useEffect(() => {
    if (bookingsError) {
      console.error('Error fetching bookings:', bookingsError);
      setNotification({
        type: 'error',
        message: 'Erreur lors du chargement des réservations',
      });
    }
  }, [bookingsError]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBookings({
      page: 1,
      search: searchTerm,
      ...filters,
    });
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Apply filters
  const applyFilters = () => {
    setCurrentPage(1);
    fetchBookings({
      page: 1,
      search: searchTerm,
      ...filters,
    });
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      booking_type: '',
      custom_price_enabled: '',
      payment_status: 'paid', // Keep showing only paid orders
    });
    setCurrentPage(1);
    fetchBookings({
      page: 1,
      search: searchTerm,
      payment_status: 'paid',
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBookings({
      page,
      search: searchTerm,
      ...filters,
    });
  };

  // Handle delete booking
  const handleDeleteBooking = (booking) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer la réservation',
      message: `Êtes-vous sûr de vouloir supprimer la réservation "${booking.booking_reference}" ? Cette action est irréversible.`,
      onConfirm: () => confirmDeleteBooking(booking.id),
    });
  };

  // Confirm delete booking
  const confirmDeleteBooking = async (id) => {
    try {
      console.log('Attempting to delete booking with ID:', id);
      await deleteBooking(id);

      setNotification({
        type: 'success',
        message: 'Réservation supprimée avec succès',
      });

      fetchBookings({
        page: currentPage,
        search: searchTerm,
        ...filters,
      });
    } catch (error) {
      console.error('Error deleting booking:', error);
      console.error('Error details:', error.message, error.status);

      setNotification({
        type: 'error',
        message: `Erreur lors de la suppression de la réservation: ${error.message || 'Erreur inconnue'}`,
      });
    } finally {
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  // Handle update booking status
  const handleUpdateStatus = async (booking, newStatus) => {
    try {
      // Construct URL correctly to avoid double slashes
      const updateStatusUrl = `${API_BASE_URL}/bookings/${booking.id}/status/`;
      console.log('Updating booking status at:', updateStatusUrl);

      const response = await fetch(updateStatusUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update booking status');
      }

      setNotification({
        type: 'success',
        message: 'Statut de la réservation mis à jour avec succès',
      });

      fetchBookings({
        page: currentPage,
        search: searchTerm,
        ...filters,
      });
    } catch (error) {
      console.error('Error updating booking status:', error);

      setNotification({
        type: 'error',
        message: 'Erreur lors de la mise à jour du statut de la réservation',
      });
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelled':
        return <Cancel className="w-5 h-5 text-red-500" />;
      case 'completed':
        return <Done className="w-5 h-5 text-blue-500" />;
      case 'pending':
      default:
        return <HourglassEmpty className="w-5 h-5 text-yellow-500" />;
    }
  };

  // Table columns
  const columns = [
    {
      key: 'booking_reference',
      label: 'Référence',
      sortable: true,
      render: (row) => (
        <div className="font-medium text-gray-900">{row.booking_reference}</div>
      ),
    },
    {
      key: 'customer',
      label: 'Client',
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{`${row.first_name} ${row.last_name}`}</div>
          <div className="text-sm text-gray-500">{row.email}</div>
        </div>
      ),
    },
    {
      key: 'booking_type',
      label: 'Type',
      sortable: true,
      render: (row) => {
        let typeName = '';
        let itemName = '';

        switch (row.booking_type) {
          case 'destination':
            typeName = 'Destination';
            itemName = row.destination_name || 'N/A';
            break;
          case 'excursion':
            typeName = 'Excursion';
            itemName = row.excursion_title || 'N/A';
            break;
          case 'package':
            typeName = 'Forfait';
            itemName = row.package_name || 'N/A';
            break;
          default:
            typeName = row.booking_type;
            itemName = 'N/A';
        }

        return (
          <div>
            <div className="font-medium text-gray-900">{typeName}</div>
            <div className="text-sm text-gray-500">{itemName}</div>
          </div>
        );
      },
    },
    {
      key: 'dates',
      label: 'Dates',
      render: (row) => (
        <div>
          {row.end_date && (
            <div className="font-medium text-gray-900">
              Fin: {new Date(row.end_date).toLocaleDateString()}
            </div>
          )}
          <div className="text-sm text-gray-500">
            Créée le: {new Date(row.created_at).toLocaleDateString()}
          </div>
        </div>
      ),
    },
    {
      key: 'price',
      label: 'Prix',
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.total_price} €</div>
          {row.custom_price_enabled && (
            <div className="text-xs text-blue-600 font-medium">Prix personnalisé</div>
          )}
          <div className="text-sm text-gray-500">{row.travelers} voyageur{row.travelers > 1 ? 's' : ''}</div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (row) => (
        <StatusBadge status={row.status} />
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/bookings/detail/${row.id}`);
            }}
            className="text-blue-600 hover:text-blue-800"
            title="Voir les détails"
          >
            <Visibility className="w-5 h-5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteBooking(row);
            }}
            className="text-red-600 hover:text-red-800"
            title="Supprimer"
          >
            <Delete className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des réservations</h1>
          <p className="text-gray-500">
            Gérez les réservations des clients
          </p>
        </div>

        {/* <Link
          to="/bookings/create"
          className="btn btn-primary flex items-center space-x-2 w-full md:w-auto justify-center"
        >
          <Add className="w-5 h-5" />
          <span>Nouvelle réservation</span>
        </Link> */}
      </div>

      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 mb-6">
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher une réservation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Search className="w-5 h-5" />
              </div>
              <button
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                Rechercher
              </button>
            </div>
          </form>

          <div className="flex items-center space-x-4">
            {/* Payment Status Toggle */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">
                Affichage:
              </label>
              <select
                value={filters.payment_status}
                onChange={(e) => {
                  const newFilters = { ...filters, payment_status: e.target.value };
                  setFilters(newFilters);
                  setCurrentPage(1);
                  fetchBookings({
                    page: 1,
                    search: searchTerm,
                    ...newFilters,
                  });
                }}
                className="input text-sm"
              >
                <option value="paid">Commandes payées uniquement</option>
                <option value="">Toutes les commandes</option>
              </select>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-outline flex items-center space-x-2"
            >
              <FilterList className="w-5 h-5" />
              <span>Filtres</span>
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label htmlFor="status" className="label">
                  Statut
                </label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="input"
                >
                  <option value="">Tous</option>
                  <option value="pending">En attente</option>
                  <option value="confirmed">Confirmée</option>
                  <option value="cancelled">Annulée</option>
                  <option value="completed">Terminée</option>
                </select>
              </div>

              <div>
                <label htmlFor="booking_type" className="label">
                  Type de réservation
                </label>
                <select
                  id="booking_type"
                  name="booking_type"
                  value={filters.booking_type}
                  onChange={handleFilterChange}
                  className="input"
                >
                  <option value="">Tous</option>
                  <option value="destination">Destination</option>
                  <option value="excursion">Excursion</option>
                  <option value="package">Forfait</option>
                </select>
              </div>

              <div>
                <label htmlFor="custom_price_enabled" className="label">
                  Prix personnalisé
                </label>
                <select
                  id="custom_price_enabled"
                  name="custom_price_enabled"
                  value={filters.custom_price_enabled}
                  onChange={handleFilterChange}
                  className="input"
                >
                  <option value="">Tous</option>
                  <option value="true">Avec prix personnalisé</option>
                  <option value="false">Prix standard</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={resetFilters}
                className="btn btn-outline"
              >
                Réinitialiser
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="btn btn-primary"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}

        <Table
          columns={columns}
          data={bookingsData?.bookings || []}
          loading={loadingBookings}
          onRowClick={(row) => navigate(`/bookings/detail/${row.id}`)}
          pagination={true}
          totalItems={bookingsData?.count || 0}
          itemsPerPage={10}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          emptyMessage="Aucune réservation trouvée"
        />
      </Card>
    </div>
  );
};

export default BookingListPage;
